/**
 * Notification Empty State
 *
 * Displayed when there are no notifications to show.
 * Provides contextual messaging based on active filters.
 *
 * @module components/notifications/NotificationEmptyState
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { BellOff } from 'lucide-react';

interface NotificationEmptyStateProps {
  /** Optional label for the active category filter */
  categoryLabel?: string;
}

/**
 * Empty state with calm, on-brand messaging
 */
const NotificationEmptyState: React.FC<NotificationEmptyStateProps> = ({
  categoryLabel,
}) => {
  const { t } = useTranslation('notifications');

  const message = categoryLabel
    ? t('empty.titleWithFilter', { category: categoryLabel.toLowerCase() })
    : t('empty.title');

  const description = categoryLabel
    ? t('empty.descriptionWithFilter')
    : t('empty.description');

  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
        <BellOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      </div>
      <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">
        {message}
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {description}
      </p>
    </div>
  );
};

export default NotificationEmptyState;
